import React from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  CreditCard,
  FileCheck2,
  Handshake,
  Building,
  DollarSign,
  TrendingUp,
  Lock,
  Clock,
  Globe2,
  Landmark,
} from "lucide-react";
import HeaderV2 from "./HeaderV2";
import FooterV2 from "./FooterV2";
import V2PageHero from "./sections/V2PageHero";
import FAQV2 from "./sections/FAQV2";
import CTAV2 from "./sections/CTAV2";
import InlineCTA from "./sections/InlineCTA";
import useSmoothScroll from "../../hooks/useSmoothScroll";

const PILLARS = [
  {
    icon: FileCheck2,
    title: "Pre-approval Readiness",
    copy: "We audit your corporate structure, UBO file and source-of-funds story before a single application is submitted.",
  },
  {
    icon: Handshake,
    title: "Direct Banker Introductions",
    copy: "You meet senior relationship managers — not branch clerks. We position your business in the right risk tier.",
  },
  {
    icon: CreditCard,
    title: "Multi-currency & Digital",
    copy: "AED, USD, EUR, GBP accounts. Traditional tier-1 banks plus digital-first options like Wio for speed.",
  },
];

const BANKS = ["EMIRATES NBD", "WIO", "MASHREQ", "FAB", "RAKBANK", "ADCB", "CBD", "HSBC UAE"];

const BENEFITS = [
  { icon: DollarSign, t: "Multi-currency accounts", d: "AED, USD, EUR, GBP and more." },
  { icon: TrendingUp, t: "Trade finance lines", d: "LCs, guarantees, working-capital loans." },
  { icon: Lock, t: "Enterprise-grade security", d: "2FA, biometric, hardware tokens." },
  { icon: Clock, t: "Same-day wires", d: "SWIFT, local FTS and RTP support." },
  { icon: Globe2, t: "International cards", d: "Corporate Visa/Mastercard with FX optimisation." },
  { icon: Landmark, t: "Regulator-grade compliance", d: "CBUAE, DFSA, FSRA aligned KYC." },
];

const DOCS = [
  "Certificate of Incorporation & trade license",
  "Memorandum of Association (MoA) & Articles of Association",
  "Shareholder & UBO passport copies + Emirates ID",
  "Bank reference letters (personal & corporate)",
  "6-month bank statements + source of funds narrative",
  "Board resolution authorising account opening",
  "Business plan with 12–24 month cash-flow projection",
  "Invoices, contracts or LOIs from target customers",
];

export default function BankingV2() {
  useSmoothScroll(true);
  return (
    <div data-testid="v2-banking-page" className="bg-white">
      <HeaderV2 />
      <main>
        <V2PageHero
          eyebrow="Banking & Corporate Support"
          title="The hardest part of UAE setup,"
          highlight="made routine."
          copy="StartINC structures your corporate banking application for approval with Emirates NBD, Wio, Mashreq, FAB, RAKBANK and leading international names."
          image="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=2400&q=80"
        />

        {/* 3 pillars */}
        <section className="bg-white py-10 md:py-14">
          <div className="container-x grid grid-cols-1 md:grid-cols-3 gap-5">
            {PILLARS.map(({ icon: Icon, title, copy }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="card-light rounded-sm p-7"
              >
                <div className="w-11 h-11 rounded-sm bg-[#002366] text-[#6B8AFF] inline-flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-heading font-semibold text-base md:text-lg tracking-tight text-[#0F172A]">
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{copy}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Banking marquee */}
        <section className="bg-[#EEF2FF] border-y border-[#3366FF]/20 py-6 md:py-8">
          <Marquee speed={36} gradient={false} pauseOnHover>
            {BANKS.concat(BANKS).map((b, i) => (
              <span
                key={`${b}-${i}`}
                className="font-heading text-2xl md:text-3xl font-black tracking-tight text-[#002366] mx-8 md:mx-14 whitespace-nowrap flex items-center gap-8 md:gap-14"
              >
                {b}
                <span className="w-1.5 h-1.5 rounded-full bg-[#3366FF]" />
              </span>
            ))}
          </Marquee>
        </section>

        <InlineCTA
          eyebrow="Banking ready when you are"
          title="Pre-audit your application before a single bank sees it."
          buttonLabel="Audit my file"
          service="banking"
        />

        {/* Benefits */}
        <section className="bg-[#F8FAFC] py-10 md:py-14">
          <div className="container-x">
            <div className="max-w-2xl mb-7">
              <p className="overline text-[#3366FF]">Account capabilities</p>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
                A <span className="font-black text-[#002366]">real corporate account</span> —
                not a prepaid wallet.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {BENEFITS.map(({ icon: Icon, t, d }, i) => (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                  className="flex gap-4 p-5 bg-white border border-slate-200 rounded-sm hover:border-[#3366FF]/40 hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 shrink-0 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-[#0F172A]">{t}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{d}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Documents */}
        <section className="bg-[#001A4D] text-white py-10 md:py-14">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline text-[#6B8AFF]">Documents we prepare</p>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
                An <span className="font-black text-[#6B8AFF]">audit-grade</span> banking file — from day one.
              </h2>
              <p className="mt-5 text-slate-300/85 leading-relaxed">
                Every StartINC banking engagement ships with a complete document
                pack, cross-verified against the bank's latest compliance matrix.
                No back-and-forth, no surprise requests after submission.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DOCS.map((d, i) => (
                <div
                  key={d}
                  className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-sm hover:border-[#6B8AFF]/50 transition-colors"
                >
                  <Building className="w-4 h-4 shrink-0 text-[#6B8AFF]" />
                  <span className="text-sm text-slate-200/90">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FAQV2 />
        <CTAV2 />
      </main>
      <FooterV2 />
    </div>
  );
}
